import { Component, inject, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { forkJoin } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators, FormArray, FormGroup } from '@angular/forms';
import { TimeLogService } from '../../../core/services/timelog.service';
import { ProjectService } from '../../../core/services/project.service';
import { CommunicationService } from '../../../core/services/communication.service';
import { AuthService } from '../../../core/services/auth.service';

@Component({
    selector: 'app-timelogs',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './timelogs.component.html',
    styleUrl: './timelogs.component.scss'
})
export class TimeLogsComponent implements OnInit {
    private timeLogService = inject(TimeLogService);
    private projectService = inject(ProjectService);
    private communicationService = inject(CommunicationService);
    private authService = inject(AuthService);
    private fb = inject(FormBuilder);
    private cdr = inject(ChangeDetectorRef);

    timeLogs: any[] = [];
    projects: any[] = []; // Only active projects for the dropdown
    allProjects: any[] = []; // All projects for name lookup
    isLoading = true;
    errorMessage = '';
    showForm = false;
    editingLog: any = null;

    // Change to FormArray to support multiple logs
    timeLogForms: FormArray = this.fb.array([]);

    // Timeline Data
    timelineData: any[] = [];

    // Infinite scroll (weekly chunks)
    pageSize = 100;
    isLoadingMore = false;
    hasMoreData = true;
    private rangeStart: Date | null = null;
    private readonly rangeDays = 11;

    // Premium color palette for timeline headers (~50 colors)
    private groupColors = [
        '#ff7900', // Brand Orange
        '#3b82f6', // Blue 500
        '#10b981', // Emerald 500
        '#8b5cf6', // Violet 500
        '#f43f5e', // Rose 500
        '#0ea5e9', // Sky 500
        '#eab308', // Yellow 500
        '#6366f1', // Indigo 500
        '#ec4899', // Pink 500
        '#14b8a6', // Teal 500
        '#f97316', // Orange 500
        '#84cc16', // Lime 500
        '#06b6d4', // Cyan 500
        '#a855f7', // Purple 500
        '#d946ef', // Fuchsia 500
        '#ef4444', // Red 500
        '#f59e0b', // Amber 500
        '#22c55e', // Green 500
        '#2563eb', // Blue 600
        '#7c3aed', // Violet 600
        '#db2777', // Pink 600
        '#ea580c', // Orange 600
        '#65a30d', // Lime 600
        '#0891b2', // Cyan 600
        '#9333ea', // Purple 600
        '#c026d3', // Fuchsia 600
        '#dc2626', // Red 600
        '#d97706', // Amber 600
        '#16a34a', // Green 600
        '#4f46e5', // Indigo 600
        '#be123c', // Rose 700
        '#0284c7', // Sky 600
        '#b45309', // Amber 700
        '#4d7c0f', // Lime 700
        '#0e7490', // Cyan 700
        '#7e22ce', // Purple 700
        '#a21caf', // Fuchsia 700
        '#b91c1c', // Red 700
        '#c2410c', // Orange 700
        '#15803d', // Green 700
        '#1d4ed8', // Blue 700
        '#6d28d9', // Violet 700
        '#be185d', // Pink 700
        '#0369a1', // Sky 700
        '#0f766e', // Teal 700
        '#4338ca', // Indigo 700
        '#e11d48', // Rose 600
        '#fb923c', // Orange 400
        '#38bdf8', // Sky 400
        '#818cf8', // Indigo 400
        '#4ade80', // Green 400
        '#a78bfa', // Violet 400
        '#f472b6'  // Pink 400
    ];

    taskTypes = ['Maintenance', 'Bug Fix', 'New Feature', 'Revision', "Support", "Meeting", "Automation", "Test"];

    ngOnInit() {
        this.loadData();
    }

    loadData() {
        this.isLoading = true;
        this.isLoadingMore = false;
        this.errorMessage = '';
        this.timeLogs = [];
        this.timelineData = [];
        this.hasMoreData = true;
        console.log('Loading initial data...');

        const initialRange = this.getInitialRange();
        this.rangeStart = initialRange.start;

        forkJoin({
            logs: this.timeLogService.getMyLogsByDateRange(initialRange.start, initialRange.end, 1, this.pageSize),
            projects: this.projectService.getAll()
        }).subscribe({
            next: (results: any) => {
                console.log('Data loaded successfully', results);

                // Process Projects
                const projectItems = Array.isArray(results.projects) ? results.projects : (results.projects?.data || results.projects?.items || []);
                this.allProjects = projectItems;
                this.projects = projectItems.filter((p: any) => p.status === 1);

                // Process Logs (support both paged and non-paged)
                const logItems = this.extractLogItems(results.logs);
                this.timeLogs = this.mapLogItems(logItems);

                // Process Timeline now that we have both
                this.processTimelineData();
                this.isLoading = false;
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Failed to load data', err);
                this.errorMessage = 'Failed to load data. Please try again.';
                this.isLoading = false;
                this.cdr.detectChanges();
            }
        });
    }

    // ... existing properties ...

    createTimeLogGroup(log: any = null): FormGroup {
        const group = this.fb.group({
            id: [log?.id || ''], // Hidden field for ID
            projectId: [log?.projectId || '', Validators.required],
            taskType: [log?.taskType || ''], // Only for Hartis
            date: [log?.date?.split('T')[0] || new Date().toISOString().split('T')[0], Validators.required],
            hours: [log?.displayDuration?.replace(/\s/g, '') || '', [
                Validators.required,
                this.durationValidator.bind(this)
            ]],
            description: [log?.description || '', Validators.required]
        });

        // Add subscription for project changes to handle validation
        group.get('projectId')?.valueChanges.subscribe({
            next: (projectId) => {
                const taskTypeControl = group.get('taskType');
                if (this.isHartisProject(projectId)) {
                    taskTypeControl?.setValidators([Validators.required]);
                } else {
                    taskTypeControl?.clearValidators();
                }
                taskTypeControl?.updateValueAndValidity();
            }
        });

        // Update validity of hours when date changes
        group.get('date')?.valueChanges.subscribe(() => {
            group.get('hours')?.updateValueAndValidity();
        });

        // Trigger initial validation check safely after creation
        setTimeout(() => {
            const initialProjectId = group.get('projectId')?.value;
            if (initialProjectId) {
                // Determine if Hartis
                const isHartis = this.isHartisProject(initialProjectId);
                const taskTypeControl = group.get('taskType');

                if (isHartis) {
                    taskTypeControl?.setValidators([Validators.required]);
                } else {
                    taskTypeControl?.clearValidators();
                }
                taskTypeControl?.updateValueAndValidity();
            }
        });

        return group;
    }

    getLoggedHoursForDate(date: string, excludeLogId?: string): number {
        const dateStr = date.split('T')[0];
        return this.timeLogs
            .filter(log => {
                const logDate = log.date.split('T')[0];
                return logDate === dateStr && log.id !== excludeLogId;
            })
            .reduce((sum, log) => sum + (Number(log.hours) || 0), 0);
    }

    get timeLogControls() {
        return this.timeLogForms.controls as FormGroup[];
    }

    addLogEntry() {
        this.timeLogForms.push(this.createTimeLogGroup());
    }

    removeLogEntry(index: number) {
        this.timeLogForms.removeAt(index);
        // If we remove the last one, and we are not in strictly "editing" mode (or maybe we are),
        // we might want to close the form if empty.
        if (this.timeLogForms.length === 0) {
            this.showForm = false;
            this.editingLog = null;
        }
    }

    reloadInitialLogs() {
        // Reload from today back to the last 7 days
        this.isLoading = true;
        this.isLoadingMore = false;
        this.errorMessage = '';
        this.timeLogs = [];
        this.timelineData = [];
        this.hasMoreData = true;

        const initialRange = this.getInitialRange();
        this.rangeStart = initialRange.start;

        this.timeLogService.getMyLogsByDateRange(initialRange.start, initialRange.end, 1, this.pageSize).subscribe({
            next: (data: any) => {
                const items = this.extractLogItems(data);
                this.timeLogs = this.mapLogItems(items);
                this.processTimelineData(); // Projects should already be loaded
                this.isLoading = false;
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Failed to reload time logs', err);
                this.isLoading = false;
            }
        });
    }

    @HostListener('window:scroll', [])
    onWindowScroll() {
        if (this.isLoading || this.isLoadingMore || !this.hasMoreData) {
            return;
        }

        const thresholdPx = 250;
        const scrolled = window.innerHeight + window.scrollY;
        const pageHeight = document.documentElement.scrollHeight || document.body.scrollHeight;

        if (pageHeight - scrolled <= thresholdPx) {
            this.loadMoreLogs();
        }
    }

    loadMoreLogs() {
        if (this.isLoading || this.isLoadingMore || !this.rangeStart || !this.hasMoreData) {
            return;
        }

        this.isLoadingMore = true;
        const nextRange = this.getPreviousRange();

        this.timeLogService.getMyLogsByDateRange(nextRange.start, nextRange.end, 1, this.pageSize).subscribe({
            next: (data: any) => {
                const items = this.extractLogItems(data);
                const mapped = this.mapLogItems(items);
                if (mapped.length === 0) {
                    this.hasMoreData = false;
                }
                this.appendLogs(mapped);
                this.rangeStart = nextRange.start;
                this.processTimelineData();
                this.isLoadingMore = false;
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Failed to load more time logs', err);
                this.isLoadingMore = false;
            }
        });
    }

    processTimelineData() {
        // Group logs by Date
        const groupedByDate = this.timeLogs.reduce((acc, log) => {
            const dateStr = new Date(log.date).toISOString().split('T')[0];
            if (!acc[dateStr]) {
                acc[dateStr] = [];
            }
            acc[dateStr].push(log);
            return acc;
        }, {} as any);

        // Map to store consistent colors for users across dates
        const userColorMap: { [key: string]: string } = {};
        let colorIndex = 0;

        // Transform into array and sort by date descending
        this.timelineData = Object.keys(groupedByDate)
            .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
            .map(date => {
                const logsForDate = groupedByDate[date];

                // Group by User within Date (Even if it's just 'Me')
                const groupedByUser = logsForDate.reduce((acc: any, log: any) => {
                    const userInfo = this.authService.getUserInfo();
                    const userName = userInfo?.userName || 'Me';

                    if (!acc[userName]) {

                        // Assign consistent color
                        if (!userColorMap[userName]) {
                            userColorMap[userName] = this.groupColors[colorIndex % this.groupColors.length];
                            colorIndex++;
                        }

                        acc[userName] = {
                            name: userName,
                            logs: [],
                            totalDurationMinutes: 0,
                            projectDurations: {} as { [key: string]: number },
                            avatarLetter: (userName.charAt(0) || 'M').toUpperCase(),
                            color: userColorMap[userName]
                        };
                    }
                    acc[userName].logs.push(log);

                    const durationMins = this.parseDurationToMinutes(log.duration);
                    acc[userName].totalDurationMinutes += durationMins;

                    // Aggregate per project
                    const projectName = this.getProjectName(log.projectId);
                    if (!acc[userName].projectDurations[projectName]) {
                        acc[userName].projectDurations[projectName] = 0;
                    }
                    acc[userName].projectDurations[projectName] += durationMins;

                    return acc;
                }, {});

                const users = Object.values(groupedByUser).map((user: any) => {
                    // Format project chips data
                    const projects = Object.keys(user.projectDurations).map(projName => ({
                        name: this.getProjectShortName(projName),
                        duration: this.formatMinutesToDuration(user.projectDurations[projName])
                    }));

                    const projectStrings = projects.map(p => `${p.name} ${p.duration}`);

                    // Create the full header string
                    const headerDisplay = [user.name, ...projectStrings].join(' | ');

                    return {
                        ...user,
                        headerDisplay: headerDisplay,
                        projects: projects,
                        totalDuration: this.formatMinutesToDuration(user.totalDurationMinutes),
                        expanded: false // Initially collapsed
                    };
                });

                return {
                    date: new Date(date),
                    users: users,
                    expanded: true // Date expanded by default
                };
            });
    }

    private getProjectShortName(name: string): string {
        const mapping: { [key: string]: string } = {
            'SoreLogic': 'SORE',
            'ECG Analysis': 'ECG'
        };
        // If name is too long, maybe truncate?
        return mapping[name] || name;
    }

    private parseDurationToMinutes(duration: string): number {
        if (!duration) return 0;
        const hoursMatch = duration.match(/(\d+)H/);
        const minutesMatch = duration.match(/(\d+)M/);
        const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
        const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
        return hours * 60 + minutes;
    }

    formatMinutesToDuration(totalMinutes: number): string {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = Math.round(totalMinutes % 60);

        const parts = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);

        return parts.length > 0 ? parts.join(' ') : '0h';
    }

    toggleDate(dateItem: any) {
        dateItem.expanded = !dateItem.expanded;
    }

    toggleUser(userItem: any, event: Event) {
        event.stopPropagation();
        userItem.expanded = !userItem.expanded;
    }

    loadProjects() {
        this.projectService.getAll().subscribe({
            next: (data: any) => {
                const items = Array.isArray(data) ? data : (data?.data || data?.items || []);
                this.allProjects = items;
                this.projects = items.filter((p: any) => p.status === 1);
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Failed to load projects', err);
            }
        });
    }

    private durationValidator(control: any): { [key: string]: any } | null {
        const value = control.value;
        if (!value) return null;

        // Allow pattern like: 1w, 2d, 3h, 30m, or combinations like "1d 4h 30m"
        const pattern = /^(\d+(?:\.\d+)?[wdhm]\s*)+$/;
        if (!pattern.test(value.toLowerCase().replace(/\s+/g, ''))) {
            return { invalidDuration: true };
        }

        return null;
    }

    private parseDuration(input: string): number {
        const WORK_DAY_HOURS = 8;
        const WORK_WEEK_HOURS = 40;

        // Remove all spaces and convert to lowercase
        const normalized = input.toLowerCase().replace(/\s+/g, '');

        let totalMinutes = 0;

        // Parse weeks (1w = 40 hours)
        const weekMatch = normalized.match(/(\d+(?:\.\d+)?)w/);
        if (weekMatch) {
            totalMinutes += parseFloat(weekMatch[1]) * WORK_WEEK_HOURS * 60;
        }

        // Parse days (1d = 8 hours)
        const dayMatch = normalized.match(/(\d+(?:\.\d+)?)d/);
        if (dayMatch) {
            totalMinutes += parseFloat(dayMatch[1]) * WORK_DAY_HOURS * 60;
        }

        // Parse hours
        const hourMatch = normalized.match(/(\d+(?:\.\d+)?)h/);
        if (hourMatch) {
            totalMinutes += parseFloat(hourMatch[1]) * 60;
        }

        // Parse minutes
        const minuteMatch = normalized.match(/(\d+(?:\.\d+)?)m/);
        if (minuteMatch) {
            totalMinutes += parseFloat(minuteMatch[1]);
        }

        return totalMinutes / 60; // Return as hours
    }

    toggleForm() {
        if (this.showForm) {
            this.showForm = false;
            this.editingLog = null;
            this.timeLogForms.clear();
        } else {
            this.showForm = true;
            this.editingLog = null;
            this.timeLogForms.clear();
            this.addLogEntry();
        }
    }

    editLog(log: any) {
        this.editingLog = log;
        this.showForm = true;
        this.timeLogForms.clear();
        this.timeLogForms.push(this.createTimeLogGroup(log));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onSubmit(index: number, isDraft: boolean = false) {
        const formGroup = this.timeLogForms.at(index) as FormGroup;

        // For drafts, allow saving even with invalid form. For regular submits, require valid form.
        if (!isDraft && !formGroup.valid) {
            return;
        }

        this.isLoading = true;

        // Convert date to UTC ISO string for PostgreSQL
        const formValue = formGroup.value;
        
        // For drafts, use today's date if date is not provided
        let dateToUse = formValue.date;
        if (!dateToUse && isDraft) {
            dateToUse = new Date().toISOString().split('T')[0];
        }
        const localDate = new Date(dateToUse!);

        // Parse Jira-style duration (e.g., "1h30m", "2d", "45m") to hours
        // For drafts, use 0 hours if duration is not provided or invalid
        let totalHours = 0;
        if (formValue.hours) {
            try {
                totalHours = this.parseDuration(formValue.hours);
            } catch (e) {
                // If parsing fails and it's not a draft, return early
                if (!isDraft) {
                    this.isLoading = false;
                    return;
                }
                // For drafts, use 0 hours if parsing fails
                totalHours = 0;
            }
        }

        // Convert to PostgreSQL interval format (ISO 8601 duration)
        const hours = Math.floor(totalHours);
        const minutes = Math.round((totalHours - hours) * 60);
        const duration = `PT${hours}H${minutes}M`;

        const timeLogData = {
            projectId: formValue.projectId || null,
            date: localDate.toISOString(),
            duration: duration, // PostgreSQL interval format
            description: formValue.description || '',
            taskType: formValue.taskType || null,
            isDraft: isDraft
        };

        const successCallback = () => {
            this.isLoading = false;
            // Plan: Remove the submitted form from the array
            this.removeLogEntry(index);
            this.reloadInitialLogs();
            console.log('Time log saved successfully');

            // NOTIFY MANAGER block removed - handled by backend


            // If we edited a single log, closing the form is handled by removeLogEntry -> length 0 check
        };

        const errorCallback = (err: any) => {
            console.error('Failed to save time log', err);
            this.errorMessage = 'Failed to save time log. Please try again.';
            this.isLoading = false;
            this.cdr.detectChanges();
        };

        if (this.editingLog) {
            // UPDATE existing log
            this.timeLogService.update(this.editingLog.id, timeLogData).subscribe({
                next: successCallback,
                error: errorCallback
            });
        } else {
            // CREATE new log
            this.timeLogService.create(timeLogData).subscribe({
                next: successCallback,
                error: errorCallback
            });
        }
    }

    get totalHours(): number {
        return this.timeLogs.reduce((sum, log) => sum + (Number(log.hours) || 0), 0);
    }

    private parseISO8601DurationToHours(duration: string): number {
        // Simple regex to parse PT1H30M
        const hoursMatch = duration.match(/(\d+)H/);
        const minutesMatch = duration.match(/(\d+)M/);

        const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
        const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;

        return hours + (minutes / 60);
    }

    private formatISO8601Duration(duration: string): string {
        const hoursMatch = duration.match(/(\d+)H/);
        const minutesMatch = duration.match(/(\d+)M/);

        const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
        const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;

        const parts = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);

        return parts.length > 0 ? parts.join(' ') : '0h';
    }

    getProjectName(projectId: string): string {
        const project = this.allProjects.find(p => p.id === projectId);
        return project?.name || 'Unknown Project';
    }

    autoResizeTextarea(event: any): void {
        const textarea = event.target;
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
    }

    isHartisProject(projectId: string): boolean {
        const project = this.allProjects.find(p => p.id === projectId);
        return project?.name === 'Hartis';
    }

    deleteLog(logId: string) {
        if (!confirm('Are you sure you want to delete this draft?')) {
            return;
        }

        this.timeLogService.delete(logId).subscribe({
            next: () => {
                console.log('Draft deleted successfully');
                // Remove from local timeLogs array
                this.timeLogs = this.timeLogs.filter(log => log.id !== logId);

                // Remove from timeline data without resetting expansion states
                for (const day of this.timelineData) {
                    for (const user of day.users) {
                        const logIndex = user.logs.findIndex((log: any) => log.id === logId);
                        if (logIndex !== -1) {
                            // Remove the log
                            user.logs.splice(logIndex, 1);

                            // Recalculate user totals
                            user.totalDurationMinutes = user.logs.reduce((sum: number, log: any) => {
                                return sum + this.parseDurationToMinutes(log.duration);
                            }, 0);
                            user.totalDuration = this.formatMinutesToDuration(user.totalDurationMinutes);

                            // Recalculate project durations
                            user.projectDurations = {};
                            user.logs.forEach((log: any) => {
                                const projectName = this.getProjectName(log.projectId);
                                if (!user.projectDurations[projectName]) {
                                    user.projectDurations[projectName] = 0;
                                }
                                user.projectDurations[projectName] += this.parseDurationToMinutes(log.duration);
                            });

                            // Update project chips
                            user.projects = Object.keys(user.projectDurations).map(projName => ({
                                name: this.getProjectShortName(projName),
                                duration: this.formatMinutesToDuration(user.projectDurations[projName])
                            }));

                            break;
                        }
                    }
                }

                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Failed to delete draft', err);
                this.errorMessage = 'Failed to delete draft. Please try again.';
                this.cdr.detectChanges();
            }
        });
    }

    private getInitialRange() {
        const today = this.getStartOfDay(new Date());
        const end = this.getEndOfDay(today);
        const start = new Date(today);
        start.setDate(start.getDate() - (this.rangeDays - 1));
        return { start: this.getStartOfDay(start), end };
    }

    private getPreviousRange() {
        const rangeEnd = this.getStartOfDay(new Date(this.rangeStart!));
        rangeEnd.setDate(rangeEnd.getDate() - 1);
        const rangeStart = new Date(rangeEnd);
        rangeStart.setDate(rangeStart.getDate() - (this.rangeDays - 1));
        return { start: this.getStartOfDay(rangeStart), end: this.getEndOfDay(rangeEnd) };
    }

    private getStartOfDay(date: Date) {
        const value = new Date(date);
        value.setHours(0, 0, 0, 0);
        return value;
    }

    private getEndOfDay(date: Date) {
        const value = new Date(date);
        value.setHours(23, 59, 59, 999);
        return value;
    }

    private extractLogItems(data: any): any[] {
        if (Array.isArray(data)) {
            return data;
        }
        if (data?.logs) {
            return data.logs;
        }
        return data?.data || data?.items || [];
    }

    private mapLogItems(items: any[]) {
        return items.map((log: any) => ({
            ...log,
            hours: log.duration ? this.parseISO8601DurationToHours(log.duration) : 0,
            displayDuration: log.duration ? this.formatISO8601Duration(log.duration) : '0h'
        }));
    }

    private appendLogs(newLogs: any[]) {
        if (newLogs.length === 0) {
            return;
        }
        const logMap = new Map(this.timeLogs.map(log => [log.id, log]));
        newLogs.forEach(log => logMap.set(log.id, log));
        this.timeLogs = Array.from(logMap.values());
    }
}

